import { css } from '@emotion/css';
import { Stack } from '@mui/material';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';

import { FlowChartProvider } from '@/components/diagrams/FlowChart';
import { TileNodeSlotsProvider } from '@/components/diagrams/FlowChart/nodes/TileNode/tileNodeSlots.context';
import { Divider } from '@/components/Divider';
import { Card } from '@/components/layout/Card/Card';
import { EditEntityDiagramTrowser } from '@/modules/entityDiagram/components/EditEntityDiagramTrowser/EditEntityDiagramTrowser';
import { EntityDiagramLegend } from '@/modules/entityDiagram/components/EntityDiagramLegend/EntityDiagramLegend';
import { EntityDiagramProvider } from '@/modules/entityDiagram/contexts/EntityDiagram.provider';
import { EntityDiagram as EntityDiagramFlowChart } from '@/modules/entityDiagram/EntityDiagram';
import { FIRST_DECEDENT_ID_QUERY_PARAM } from '@/modules/entityDiagram/EntityDiagram.constants';
import { useQueryEntityDiagram } from '@/modules/entityDiagram/hooks';
import { EntityDiagramVariant } from '@/modules/entityDiagram/types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import {
  PresentationHoverActionsProvider,
  TOOLBAR_DISPLAY_DELAY_100MS,
} from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverActions.context';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { COLORS } from '@/styles/tokens/colors';

import { useUnguardedBundleEntity } from '../../clientPresentation/ClientPresentationDesigner.context';
import { useIsDesignMode } from '../../clientPresentation/ClientPresentationDesigner.hooks';
import {
  PresentationEntityActionsProvider,
  PresentationEntityActionsProviderBase,
} from '../../clientPresentation/components/presentationEntityActions/PresentationEntityActionsProvider';
import { usePresentationEditingContext } from '../../context/PresentationEditing.context';
import { PresentationSlideRegistrationProps } from '../../presentation.types';
import { EntityDiagramChartHover } from './EntityDiagramChartHover';
import { PresentationAwareEntityDiagramTile } from './PresentationAwareEntityDiagramTile';

const styles = {
  fullHeight: css({ height: 'calc(100% - 40px)' }), // Account for the legend and borders
};

interface EntityDiagramProps {
  entityId: string;
  householdId: string;
  /**
   * Called once the diagram finishes a layout. The slide gates its scale on
   * this (via `useDiagramSlideState` or `useReadyToScale`); with `redrawKey` it
   * fires again after each remount.
   */
  registerDiagramReady?: () => void;
  /**
   * Enables the design-mode hover/edit toolbars (per-tile "Edit entity" /
   * "Edit subtrust", diagram-level "Edit diagram"). Only the interactive diagram
   * slide opts in; the read-only entity-overview summary slides leave it off.
   */
  interactiveEditing?: boolean;
  /**
   * Bumped into the diagram's React key to force a remount. The slide owns the
   * measure-before-scale dance (`useReadyToScale`) and bumps this — via
   * {@link onDiagramDataChange} — so React Flow re-initializes from scratch and
   * re-measures cleanly after an edit, which clears the stale node internals
   * that otherwise misalign edges once the slide re-applies its scale.
   */
  redrawKey?: number;
  /**
   * Fired when the diagram data changes while the diagram is already laid out
   * and scaled — i.e. an edit refetched the entity-diagram query. The slide
   * wires this to `useReadyToScale`'s `remeasure` so the remount lands against
   * the new data, not the stale layout still on screen.
   */
  onDiagramDataChange?: () => void;
  /**
   * Whether the slide has scaled the diagram (`useReadyToScale`). Drives two
   * things: hover/edit toolbars are suppressed until ready (so nothing surfaces
   * over a measuring diagram), and {@link onDiagramDataChange} only fires once
   * ready — so the progressive data settling during initial load (pour-over
   * look-through, dispositions resolving) doesn't trigger a remeasure; only a
   * post-load edit does.
   */
  readyToScale?: boolean;
  entityDiagramVariant: EntityDiagramVariant;
  registrationProps: PresentationSlideRegistrationProps;
  firstDecedentId?: string | null | undefined;
  /**
   * True when the diagram renders next to another panel (e.g. notes on
   * the diagram slide). Lets the entity diagram stretch vertically to
   * fill the tall half-width container instead of leaving whitespace.
   */
  isInHalfWidthContainer?: boolean;
  /**
   * When true, the fit sizes the diagram against the React Flow canvas rather
   * than the measured container (T1-6663). Use where the canvas is scaled to
   * fit a fixed-size slide, so the container measurement doesn't describe the
   * space the nodes actually render into.
   */
  fitToCanvas?: boolean;
}

export function EntityDiagram({
  entityId,
  householdId,
  registerDiagramReady,
  interactiveEditing = false,
  redrawKey = 0,
  onDiagramDataChange,
  readyToScale = true,
  entityDiagramVariant,
  registrationProps,
  firstDecedentId: firstDecedentIdExternal,
  isInHalfWidthContainer,
  fitToCanvas,
}: EntityDiagramProps) {
  const [searchParams] = useSearchParams();
  const firstDecedentId =
    firstDecedentIdExternal ?? searchParams.get(FIRST_DECEDENT_ID_QUERY_PARAM);

  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();
  const deathOrder = useUnguardedBundleEntity(
    registrationProps.bundleId
  )?.deathOrder;

  const [selectedPrimaryClientId, setSelectedPrimaryClientId] = useState('');
  const isDesignMode = useIsDesignMode();
  const editEntityDiagramTrowser = useTrowser(TrowserKey.EditEntityDiagram);
  const isEditDiagramOpen = editEntityDiagramTrowser.isOpen;
  // True while an entity/sub-trust editor (owned by PresentationEntityActions-
  // Provider) is open, so we can hold off remeasuring until it closes.
  const [isEntityEditorOpen, setIsEntityEditorOpen] = useState(false);

  useEffect(() => {
    if (deathOrder?.id) {
      setSelectedPrimaryClientId(deathOrder?.id);
    } else if (firstDecedentId) {
      setSelectedPrimaryClientId(firstDecedentId);
    } else {
      setSelectedPrimaryClientId(primaryClients?.[0]?.id ?? '');
    }
  }, [
    deathOrder?.id,
    entityId,
    firstDecedentId,
    primaryClients,
    registrationProps.bundleId,
    searchParams,
  ]);

  const {
    entity,
    entityDiagram,
    recipientDispositions,
    refetch: refetchDiagram,
    isAwaitingPouroverLookThrough,
  } = useQueryEntityDiagram({
    entityId,
    entityDiagramVariant,
    selectedPrimaryClientId,
  });

  const hasData =
    (primaryClients?.length ?? 0) > 0 &&
    !!entity &&
    !!selectedPrimaryClientId &&
    !isAwaitingPouroverLookThrough;

  // The hover/edit treatment only applies to the interactive diagram slide, and
  // only in design mode (never in the read-only viewer/export, nor on the
  // read-only entity-overview summary slides).
  const enableEditing = isDesignMode && interactiveEditing;

  // Tell the slide when fresh diagram data arrives so it can remeasure: an edit
  // refetches the entity-diagram query, and the slide remounts us (via
  // `redrawKey`) to re-measure against the new data rather than the stale layout
  // still on screen. Legacy callers don't pass the callback, so this is inert
  // for them.
  //
  // Gated on `readyToScale`: only data that changes once the diagram is already
  // laid out and scaled (an edit) remeasures. The progressive settling during
  // initial load — pour-over look-through, `primaryClients` / dispositions
  // resolving — happens while not-yet-ready and the diagram measures it cleanly
  // on its own, so it must not trigger a spurious remount.
  //
  // Don't remeasure while an editor is open: a sub-trust "save & switch section"
  // refetches active queries (incl. this diagram query) mid-edit, which would
  // otherwise remount/re-scale the diagram behind the open editor. Instead we
  // remember that a remeasure is owed and flush it once the editor closes — so
  // it doesn't depend on the close's refetch resolving to a *fresh* data
  // reference (which could otherwise leave a stale, misaligned layout).
  //
  // `readyToScale`, the open flag, and the callback are read from refs (kept out
  // of the deps) so the data-change effect fires only on a real query-data
  // change — not when a flag flips (which would loop) or a caller passes a fresh
  // callback.
  const readyToScaleRef = useRef(readyToScale);
  readyToScaleRef.current = readyToScale;
  const isEditorOpen = isEditDiagramOpen || isEntityEditorOpen;
  const isEditorOpenRef = useRef(isEditorOpen);
  isEditorOpenRef.current = isEditorOpen;
  const onDiagramDataChangeRef = useRef(onDiagramDataChange);
  onDiagramDataChangeRef.current = onDiagramDataChange;
  const deferredRemeasureRef = useRef(false);
  // True while a diagram refetch kicked off on editor close is in flight, so the
  // flush-on-close remeasure waits for that refetch's fresh data instead of
  // rebuilding from the still-stale current data.
  const refetchPendingRef = useRef(false);

  // A first-grantor-death switch (from the diagram legend) changes
  // `selectedPrimaryClientId`, which is in the FlowChartProvider key — so the
  // diagram already remounts and re-measures on its own. The query outputs that
  // ride along with the switch must NOT also trigger the data-change remeasure
  // below, or we'd stack a second, redundant remeasure and flicker. Detect the
  // switch during render and suppress the data-change remeasure until the
  // key-driven remount has re-measured and restored `readyToScale`.
  const prevClientIdRef = useRef(selectedPrimaryClientId);
  const clientSwitchPendingRef = useRef(false);
  if (prevClientIdRef.current !== selectedPrimaryClientId) {
    prevClientIdRef.current = selectedPrimaryClientId;
    clientSwitchPendingRef.current = true;
  }
  useEffect(() => {
    // Once the switch's remount has settled (readiness restored), stop
    // suppressing so genuine in-place edits remeasure again.
    if (readyToScale) {
      clientSwitchPendingRef.current = false;
    }
  }, [readyToScale]);

  useEffect(() => {
    if (!entity || !readyToScaleRef.current) return;
    // Editor open → defer; the flush-on-close effect runs it once it's safe.
    if (isEditorOpenRef.current) {
      deferredRemeasureRef.current = true;
      return;
    }
    // A grantor switch already remounts via the FlowChartProvider key; skip the
    // redundant remeasure and let that remount re-measure.
    if (clientSwitchPendingRef.current) return;
    onDiagramDataChangeRef.current?.();
  }, [entity, entityDiagram, recipientDispositions]);

  useEffect(() => {
    // On editor close, run any remeasure deferred during the edit.
    if (isEditorOpen || !deferredRemeasureRef.current) return;
    deferredRemeasureRef.current = false;
    // If a diagram refetch was kicked off on close (an entity/diagram save),
    // its fresh result will arrive as a data change and remeasure then. Don't
    // remeasure now: it would rebuild from the still-stale current data and, by
    // dropping `readyToScale`, cause the fresh result — arriving mid-remount —
    // to be ignored by the data-change effect, leaving the graph stale. Sub-trust
    // and cancel closes carry no such refetch, so they still flush here.
    if (refetchPendingRef.current) return;
    if (readyToScaleRef.current) {
      onDiagramDataChangeRef.current?.();
    }
  }, [isEditorOpen]);

  // The edit-entity modal refetches only the estate-waterfall query (plus a
  // cache evict that no-ops for this no-cache diagram query), so refetch the
  // diagram ourselves after an entity/diagram edit; the resulting data change
  // drives the remeasure. Flag the in-flight refetch so the flush-on-close above
  // waits for its fresh data rather than remeasuring the stale current data.
  const refetchDiagramAfterEntityEdit = useCallback(() => {
    const refetchResult = refetchDiagram?.();
    if (!refetchResult) return;
    refetchPendingRef.current = true;
    void refetchResult
      .catch(() => {
        // The refetch failed, so no fresh data change will arrive to drive the
        // remeasure that the flush-on-close skipped while it was pending. Run it
        // here so a deferred remeasure isn't lost — the diagram re-aligns with
        // the data it still has rather than staying at a stale scale.
        if (readyToScaleRef.current) {
          onDiagramDataChangeRef.current?.();
        }
      })
      .finally(() => {
        refetchPendingRef.current = false;
      });
  }, [refetchDiagram]);

  const chart = (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => (
        <EntityDiagramFlowChart
          height={height}
          width={width}
          householdId={householdId}
          entityId={entityId}
          setSelectedPrimaryClientId={setSelectedPrimaryClientId}
        />
      )}
    </AutoSizer>
  );

  // In design mode the diagram's `tile` nodes render via
  // PresentationAwareEntityDiagramTile (per-tile hover + edit toolbar). Outside
  // design mode there's no slot provider in scope, so the plain tiles render.
  const chartStack = (
    <Stack minHeight="400px" className={styles.fullHeight}>
      {chart}
    </Stack>
  );
  const diagramTree = (
    <FlowChartProvider
      key={`${entityId}-${selectedPrimaryClientId}-${redrawKey}`}
    >
      {hasData ? (
        <EntityDiagramProvider
          primaryClients={primaryClients ?? []}
          isTwoClientHousehold={isTwoClientHousehold}
          entity={entity}
          entityDiagram={entityDiagram}
          presentationMode={true}
          viewOnlyMode={true}
          isInHalfWidthContainer={isInHalfWidthContainer}
          fitToCanvas={fitToCanvas}
          registerDiagramReady={registerDiagramReady}
          selectedPrimaryClientId={selectedPrimaryClientId}
          entityDiagramVariant={entityDiagramVariant}
          recipientDispositions={recipientDispositions}
        >
          {enableEditing ? (
            <TileNodeSlotsProvider value={ENTITY_DIAGRAM_TILE_SLOTS}>
              {chartStack}
            </TileNodeSlotsProvider>
          ) : (
            chartStack
          )}
        </EntityDiagramProvider>
      ) : (
        <div data-diagramloading={true.toString()} />
      )}
    </FlowChartProvider>
  );

  // The card interior: legend, divider, then the diagram. The diagram-level
  // hover halo wraps this whole card (see below) so it includes the legend and
  // sits on the card border rather than tight against the diagram canvas.
  const card = (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <EntityDiagramLegend presentationMode={true} />
      <Divider sx={{ borderColor: COLORS.GRAY[200] }} />
      {diagramTree}
    </Card>
  );

  if (!enableEditing) {
    // Read-only viewer / export / summary slides: no hover providers in scope,
    // so everything renders plainly.
    return card;
  }

  // Interactive design mode: the hover providers power the per-tile blue-hover
  // edit toolbar and the diagram-level "Edit diagram" hover (wrapping the whole
  // card), and own the edit-diagram trowser mount.
  return (
    <EntityDiagramDesignerChrome
      householdId={householdId}
      entityId={entityId}
      entityDiagramVariant={entityDiagramVariant}
      selectedPrimaryClientId={selectedPrimaryClientId}
      readyToScale={readyToScale}
      onAfterEntityEdit={refetchDiagramAfterEntityEdit}
      onEntityEditorOpenChange={setIsEntityEditorOpen}
      isEditDiagramOpen={isEditDiagramOpen}
      onEditDiagramClosed={(didSave) => {
        // Always remeasure on close: while the diagram sat in the background
        // behind the full-screen trowser its layout can have shifted, so it
        // must remount to re-measure and re-align regardless of the outcome.
        // On save we refetch first — the editor persists node configs to the
        // separate `useQueryEntityDiagram` query, and the resulting data change
        // drives that remeasure remount. On cancel nothing changed, so trigger
        // the remeasure directly without a refetch.
        if (didSave) {
          refetchDiagramAfterEntityEdit();
        } else {
          onDiagramDataChange?.();
        }
      }}
    >
      <EntityDiagramChartHover
        onEditDiagram={() => editEntityDiagramTrowser.open()}
        isEditDiagramOpen={isEditDiagramOpen}
      >
        {card}
      </EntityDiagramChartHover>
    </EntityDiagramDesignerChrome>
  );
}

/** Stable slot identity so the slot provider doesn't remount tiles each render. */
const ENTITY_DIAGRAM_TILE_SLOTS = { Tile: PresentationAwareEntityDiagramTile };

interface EntityDiagramDesignerChromeProps {
  householdId: string;
  entityId: string;
  entityDiagramVariant: EntityDiagramVariant;
  selectedPrimaryClientId: string;
  /** The edit-diagram trowser is open (a tile/diagram hover opened it). */
  isEditDiagramOpen: boolean;
  /**
   * The trowser has closed, whatever dismissed it; on save (didSave) refetch so
   * the new layout shows. Closing itself is this component's job.
   */
  onEditDiagramClosed: (didSave?: boolean) => void;
  /** Whether the slide has scaled the diagram; hovers are off until ready. */
  readyToScale: boolean;
  /** Refetch the diagram query after an entity edit so its tiles refresh. */
  onAfterEntityEdit: () => void;
  /** Reports when an entity/sub-trust editor opens or closes. */
  onEntityEditorOpenChange: (open: boolean) => void;
}

/**
 * Design-mode-only chrome around the presentation entity diagram: supplies the
 * shared hover-action + entity-row-action providers (powering the per-tile
 * "Edit entity"/"Edit subtrust" toolbars) and owns the full-screen
 * `EditEntityDiagramTrowser` opened from the diagram-level "Edit diagram"
 * hover.
 *
 * Mirrors the design-mode wiring in `EstateWaterfallDiagramSlideBody`.
 */
function EntityDiagramDesignerChrome({
  householdId,
  entityId,
  entityDiagramVariant,
  selectedPrimaryClientId,
  isEditDiagramOpen,
  onEditDiagramClosed,
  readyToScale,
  onAfterEntityEdit,
  onEntityEditorOpenChange,
  children,
}: PropsWithChildren<EntityDiagramDesignerChromeProps>) {
  const { close: closeEditDiagram } = useTrowser(TrowserKey.EditEntityDiagram);

  // The trowser closes by two routes: its own Cancel/Save, which lands in the
  // `onClose` below, and a browser Back, which just clears `modalOpen` and
  // unmounts it with no callback at all. So the close is *reported* off the
  // open→closed transition — covering both — while `onClose` only records what
  // the dismissal carried. Child effects run before the parent's, so a save's
  // refetch is already in flight when the parent's flush-on-close checks for it.
  const didSaveRef = useRef(false);
  const wasOpenRef = useRef(isEditDiagramOpen);
  const onEditDiagramClosedRef = useRef(onEditDiagramClosed);
  onEditDiagramClosedRef.current = onEditDiagramClosed;
  useEffect(() => {
    const justClosed = wasOpenRef.current && !isEditDiagramOpen;
    wasOpenRef.current = isEditDiagramOpen;
    if (!justClosed) return;

    const didSave = didSaveRef.current;
    didSaveRef.current = false;
    onEditDiagramClosedRef.current(didSave);
  }, [isEditDiagramOpen]);

  // Outside the designer (the single-entity export preview), the editing context
  // supplies householdId + the surface's own refresh; the designer mounts no such
  // context, so we fall back to the designer-coupled provider adapter.
  const editing = usePresentationEditingContext();

  const actions = editing ? (
    <PresentationEntityActionsProviderBase
      householdId={editing.householdId}
      onRefresh={editing.onRefresh}
      onAfterMutation={onAfterEntityEdit}
      onEditorOpenChange={onEntityEditorOpenChange}
    >
      {children}
    </PresentationEntityActionsProviderBase>
  ) : (
    <PresentationEntityActionsProvider
      onAfterMutation={onAfterEntityEdit}
      onEditorOpenChange={onEntityEditorOpenChange}
    >
      {children}
    </PresentationEntityActionsProvider>
  );

  return (
    <>
      <PresentationHoverActionsProvider
        toolbarDisplayDelayMS={TOOLBAR_DISPLAY_DELAY_100MS}
        disabled={!readyToScale}
      >
        {actions}
      </PresentationHoverActionsProvider>
      {isEditDiagramOpen && (
        // Slides render under the tenant's CustomerThemeProvider; keep the
        // editor chrome Luminary-branded.
        <LuminaryThemeProvider>
          <EditEntityDiagramTrowser
            householdId={householdId}
            entityId={entityId}
            entityDiagramVariant={entityDiagramVariant}
            selectedPrimaryClientId={selectedPrimaryClientId}
            onClose={(didSave) => {
              didSaveRef.current = Boolean(didSave);
              closeEditDiagram();
            }}
          />
        </LuminaryThemeProvider>
      )}
    </>
  );
}
